/**
 * O pagina in plus a pietei („Despre", „Contact", „Rezervări").
 *
 * Paginile astea nu sunt rute in cod: sunt declarate in `config.layout.pages` si
 * compuse din aceleasi blocuri ca pagina de start. Ruta lor e `/p/<slug>`, cu
 * prefix, si asta e deliberat — fara el, un slug scris „cos" sau „finalizare" ar
 * fi acoperit o pagina reala a aplicatiei, iar clientul ar fi ramas fara coș
 * pentru ca cineva a numit o pagina de prezentare la fel.
 *
 * O pagina ceruta si nedeclarata NU se randeaza goala: raspunde ca nu exista.
 * O pagina goala ar arata identic cu una publicata gresit.
 */

import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { BlockStack, resolvePageBlocks, type BlockContext } from "../blocks";
import { carriesHeading } from "../blocks/heading";
import { formatAddress } from "../components/AddressForm";
import { useAddressSheet } from "../components/AddressSheetProvider";
import { useBootstrap } from "../components/BootstrapProvider";
import { useCart } from "../components/CartProvider";
import { EmptyState } from "../components/StateScreen";

export function Page() {
  const { slug } = useParams<{ slug: string }>();
  const { config, t } = useBootstrap();
  const { session, selectStorefront } = useCart();
  const navigate = useNavigate();
  const addressSheet = useAddressSheet();

  const wanted = (slug ?? "").trim().toLowerCase();
  const page = useMemo(
    () => (config.layout.pages ?? []).find((item) => item.slug.trim().toLowerCase() === wanted) ?? null,
    [config.layout.pages, wanted],
  );
  const blocks = useMemo(() => (page === null ? [] : resolvePageBlocks(config, wanted)), [config, page, wanted]);

  const ctx = useMemo<BlockContext>(
    () => ({
      requestAddress: () => addressSheet.open(),
      addressLabel: session.address === null ? null : formatAddress(session.address),
      openStorefront: (target: string) => {
        selectStorefront(target);
        navigate(`/vitrina/${encodeURIComponent(target)}`);
      },
      t,
    }),
    [addressSheet, session.address, selectStorefront, navigate, t],
  );

  if (page === null) {
    return (
      <EmptyState
        title="Pagina nu există"
        description="Adresa deschisă nu corespunde niciunei pagini publicate pe această platformă."
      />
    );
  }

  // Only a cover with a guaranteed heading replaces the page title. Dynamic
  // sections can disappear when data is absent and must retain the fallback.
  const first = blocks[0];
  const coverOwnsHeading = first !== undefined
    && (first.type === "hero" || first.type === "hero-search") && carriesHeading(first, 0);
  return (
    <div className="flex flex-col gap-6">
      {coverOwnsHeading ? null : <h1 className="text-2xl font-bold text-ink sm:text-3xl">{page.title}</h1>}
      <BlockStack blocks={blocks} ctx={ctx} ownsHeading={coverOwnsHeading} fallbackHeading={page.title} />
    </div>
  );
}
